// src/Dashboard/pages/teacher/ListingAdherents/ListingAdherentsDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";

import { useListingAdherents } from "./hooks/useListingAdherents";

import ListingAdherentsDashboardHeader from "./components/ListingAdherentsDashboardHeader";
import ListingAdherentsFilters from "./components/ListingAdherentsFilters";
import ListingAdherentsKpiCards from "./components/ListingAdherentsKpiCards";
import ListingAdherentsBirthdaysCard from "./components/ListingAdherentsBirthdaysCard";
import ListingAdherentsGroupToolbar from "./components/ListingAdherentsGroupToolbar";
import ListingAdherentsCardsGrid from "./components/AdherentsCardsGrid";
import ListingAdherentsEmptyState from "./components/ListingAdherentsEmptyState";
import ListingAdherentDrawer from "./components/AdherentQuickDrawer";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getRoleId(user, storedUser) {
  return Number(
    user?.role_id ||
      user?.roleId ||
      storedUser?.role_id ||
      storedUser?.roleId ||
      0,
  );
}

function UnauthorizedPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Listing des adhérents
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs et enseignants.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ListingAdherentsDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getRoleId(user, storedUser);

  const authorized = roleId === 1 || roleId === 3;
  const isAdmin = roleId === 1;
  const isTeacher = roleId === 3;

  const {
    adherents,
    filteredAdherents,
    displayedAdherents,
    stats,

    filters,
    setFilters,
    resetFilters,

    seasons,
    dojos,
    groups,

    birthdayAdherents,
    birthdaySet,

    loading,
    saving,
    deletingId,

    hasMore,
    loadMore,

    drawerOpen,
    drawerMode,
    currentAdherent,

    openDetailsDrawer,
    openEditDrawer,
    openGroupsDrawer,
    openPhotoDrawer,
    closeDrawer,

    handleUpdateAdherent,
    handleUpdatePhoto,
    handleUpdateGroups,
    handleUpdateTeacherNotes,
    handleUpdateBlessure,
    handleDeleteAdherent,

    handleCopyEmails,
    handleExportCsv,

    refresh,
  } = useListingAdherents();

  if (!authorized) {
    return <UnauthorizedPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <ListingAdherentsDashboardHeader
          onRefresh={refresh}
          loading={loading}
          isAdmin={isAdmin}
          isTeacher={isTeacher}
        />

        <ListingAdherentsFilters
          filters={filters}
          setFilters={setFilters}
          seasons={seasons}
          dojos={dojos}
          groups={groups}
          loading={loading}
          onRefresh={refresh}
          onReset={resetFilters}
        />

        <ListingAdherentsKpiCards stats={stats} loading={loading} />

        <ListingAdherentsBirthdaysCard
          adherents={birthdayAdherents}
          loading={loading}
          onOpenAdherent={openDetailsDrawer}
        />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Liste adhérents
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Vue terrain pour les enseignants
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Affichage sous forme de cartes pour accéder rapidement à la
                  fiche d’un adhérent, modifier sa photo, gérer ses groupes,
                  consulter ses informations et suivre les blessures ou
                  absences.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Résultats :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filteredAdherents.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {" "}
                  / {adherents.length}
                </span>
              </div>
            </div>

            <div className="mt-5">
              <ListingAdherentsGroupToolbar
                filters={filters}
                count={filteredAdherents.length}
                total={adherents.length}
                onCopyEmails={() => handleCopyEmails(filteredAdherents)}
                onExportCsv={() => handleExportCsv(filteredAdherents)}
              />
            </div>

            {loading ? (
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {[...Array(10)].map((_, index) => (
                  <div
                    key={index}
                    className="h-[310px] animate-pulse rounded-[24px] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                  />
                ))}
              </div>
            ) : filteredAdherents.length === 0 ? (
              <ListingAdherentsEmptyState onReset={resetFilters} />
            ) : (
              <div className="mt-5">
                <ListingAdherentsCardsGrid
                  adherents={displayedAdherents}
                  total={filteredAdherents.length}
                  birthdaySet={birthdaySet}
                  hasMore={hasMore}
                  deletingId={deletingId}
                  onLoadMore={loadMore}
                  onOpenDetails={openDetailsDrawer}
                  onEdit={openEditDrawer}
                  onManageGroups={openGroupsDrawer}
                  onChangePhoto={openPhotoDrawer}
                  onDelete={isAdmin ? handleDeleteAdherent : null}
                  isAdmin={isAdmin}
                  isTeacher={isTeacher}
                />
              </div>
            )}
          </div>
        </section>
      </div>

      <ListingAdherentDrawer
        isOpen={drawerOpen}
        mode={drawerMode}
        adherent={currentAdherent}
        groups={groups}
        seasons={seasons}
        saving={saving}
        deletingId={deletingId}
        isAdmin={isAdmin}
        isTeacher={isTeacher}
        onClose={closeDrawer}
        onOpenDetails={() => openDetailsDrawer(currentAdherent)}
        onOpenEdit={() => openEditDrawer(currentAdherent)}
        onOpenGroups={() => openGroupsDrawer(currentAdherent)}
        onOpenPhoto={() => openPhotoDrawer(currentAdherent)}
        onSubmitAdherent={handleUpdateAdherent}
        onSubmitPhoto={handleUpdatePhoto}
        onSubmitGroups={handleUpdateGroups}
        onSubmitTeacherNotes={handleUpdateTeacherNotes}
        onSubmitBlessure={handleUpdateBlessure}
        onDelete={isAdmin ? handleDeleteAdherent : null}
      />

      <ToastContainer />
    </section>
  );
}
